import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "./ui/dialog";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceDot,
} from "recharts";
import { Sparkles, TrendingDown, Minus, TrendingUp } from "lucide-react";
import { getCompletionSynopsis } from "../lib/completionSynopsis";

// Floating screen shown when the user lands on the final task of an intervention.
// Graphically summarizes distress across the session and offers a warm, tailored
// congratulations + synopsis specific to the intervention.
export const CompletionSummary = ({
  open,
  onClose,
  interventionType,
  interventionTitle,
  readings,
}) => {
  const synopsis = useMemo(
    () => getCompletionSynopsis(interventionType, readings),
    [interventionType, readings]
  );

  const chartData = useMemo(
    () =>
      (readings || [])
        .filter((r) => typeof r.distress === "number")
        .map((r, i) => ({
          step: r.label || `Step ${i + 1}`,
          distress: r.distress,
        })),
    [readings]
  );

  const hasDelta = typeof synopsis.delta === "number";
  const improved = hasDelta && synopsis.delta > 0;
  const worsened = hasDelta && synopsis.delta < 0;

  const deltaPill = hasDelta ? (
    <div
      className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium"
      style={{
        backgroundColor: improved
          ? "rgba(139, 168, 136, 0.12)"
          : worsened
          ? "rgba(217, 136, 128, 0.12)"
          : "rgba(130, 160, 216, 0.12)",
        color: improved ? "#5E8163" : worsened ? "#B56A62" : "#5F7AB0",
      }}
      data-testid="completion-delta-pill"
    >
      {improved ? (
        <TrendingDown className="w-4 h-4" />
      ) : worsened ? (
        <TrendingUp className="w-4 h-4" />
      ) : (
        <Minus className="w-4 h-4" />
      )}
      {improved
        ? `Distress ${synopsis.start} → ${synopsis.end}  ·  −${synopsis.delta}`
        : worsened
        ? `Distress ${synopsis.start} → ${synopsis.end}  ·  +${Math.abs(synopsis.delta)}`
        : `Distress held at ${synopsis.start}`}
    </div>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="sm:max-w-2xl p-0 overflow-hidden border-0 shadow-[0_30px_80px_rgba(0,0,0,0.18)] rounded-3xl"
        data-testid="completion-summary-modal"
      >
        {/* Warm gradient header */}
        <div className="relative px-7 pt-8 pb-6 bg-gradient-to-br from-[#3090A8] via-[#9DB8A0] to-[#78C0D8] text-white overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-12 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-[11px] font-medium tracking-wide mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              {interventionTitle ? `${interventionTitle} — complete` : "Session complete"}
            </div>
            <DialogTitle className="font-outfit font-semibold text-2xl sm:text-3xl leading-tight">
              {synopsis.headline}
            </DialogTitle>
            <DialogDescription className="text-white/90 text-sm sm:text-base leading-relaxed mt-3">
              {synopsis.opener}
            </DialogDescription>
          </div>
        </div>

        {/* Chart + synopsis body */}
        <div className="px-7 py-6 bg-white">
          {/* Delta pill */}
          {deltaPill && <div className="mb-4">{deltaPill}</div>}

          {/* Line chart */}
          {chartData.length >= 2 ? (
            <div
              className="rounded-2xl border border-slate-100 bg-[#FAFBFC] p-4 pr-2"
              data-testid="completion-chart"
            >
              <p className="text-[11px] uppercase tracking-wider text-[#3090A8] font-medium mb-3 pl-1">
                Your distress across the session
              </p>
              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 8, right: 12, left: -14, bottom: 6 }}
                  >
                    <defs>
                      <linearGradient id="distressLine" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#78C0D8" />
                        <stop offset="100%" stopColor="#3090A8" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#E2E8E4"
                    />
                    <XAxis
                      dataKey="step"
                      tick={{ fill: "#718096", fontSize: 11 }}
                      axisLine={{ stroke: "#E2E8E4" }}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, 10]}
                      tick={{ fill: "#718096", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                    />
                    <Line
                      type="monotone"
                      dataKey="distress"
                      stroke="url(#distressLine)"
                      strokeWidth={3}
                      dot={{ fill: "#ffffff", stroke: "#3090A8", strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: "#3090A8", stroke: "#ffffff", strokeWidth: 2 }}
                      isAnimationActive
                      animationBegin={250}
                      animationDuration={4000}
                      animationEasing="ease-in-out"
                    />
                    {chartData.length > 0 && (
                      <ReferenceDot
                        x={chartData[chartData.length - 1].step}
                        y={chartData[chartData.length - 1].distress}
                        r={6}
                        fill="#3090A8"
                        stroke="#ffffff"
                        strokeWidth={2}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div
              className="rounded-2xl border border-dashed border-slate-200 bg-[#FAFBFC] p-5 text-center text-sm text-[#718096]"
              data-testid="completion-chart-empty"
            >
              No distress readings yet — next time you'll see your full trajectory here.
            </div>
          )}

          {/* Trajectory synopsis + closer */}
          <div className="mt-5 space-y-3">
            <p className="text-[#1B2349] leading-relaxed" data-testid="completion-trajectory">
              {synopsis.trajectory}
            </p>
            <p className="text-[#1B2349] leading-relaxed italic" data-testid="completion-closer">
              {synopsis.closer}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompletionSummary;
